import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { FiltersMap } from '@wix/bex-core';
import {
  NestedTableDataItem,
  NestedTableNodeState,
} from '../../state/NestedTableState/NestedTableNodeState';
import { Box, TextButton } from '@wix/design-system';
import { ChevronRightSmall } from '@wix/wix-ui-icons-common';
import { Rotate } from '../Rotate';
import { MainCellLayout } from './MainCellLayout';

export interface NestedTableParentCellProps<
  C extends string,
  T,
  F extends FiltersMap,
> {
  rowNum: number;
  state: NestedTableNodeState<C, T, F>;
  item: NestedTableDataItem<C, T, F>;
}

function _NestedTableParentCell<C extends string, T, F extends FiltersMap>(
  props: NestedTableParentCellProps<C, T, F>,
) {
  const { state: parentState, item, rowNum } = props;

  const {
    node: { collection },
  } = parentState;

  const { originalKeyedItem } = item;

  const renderProps = parentState.renderMainColumn?.(originalKeyedItem.item);

  const [state] = useState(() =>
    item.state.createNode({
      parent: originalKeyedItem,
    }),
  );

  useEffect(() => {
    if (!state) {
      return;
    }
    state._rowNum = rowNum;
  }, [rowNum]);

  const _expanded = state?._expanded;

  return (
    <Box
      dataHook="indention-wrapper"
      paddingInlineStart={parentState.depth * 28}
      minWidth={0}
    >
      <MainCellLayout
        expandCollapseButton={
          <Box
            paddingInlineEnd="12px"
            visibility={
              state == null || state._isNotExpandableWhenEmpty
                ? 'hidden'
                : undefined
            }
            dataHook="expand-collapse-wrapper"
          >
            <TextButton
              size="small"
              dataHook="expand-collapse"
              prefixIcon={
                <Rotate timeout={200} deg={_expanded ? 90 : 0}>
                  <ChevronRightSmall
                    data-hook="chevron-icon"
                    style={{ paddingRight: 0, paddingInlineEnd: 0 }}
                  />
                </Rotate>
              }
            />
          </Box>
        }
        image={renderProps?.image}
        title={
          renderProps?.title ?? collection.itemName(originalKeyedItem.item)
        }
        subtitle={renderProps?.subtitle}
      />
    </Box>
  );
}

export const NestedTableParentCell = observer(_NestedTableParentCell);
